// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators move among Schemas, Datasets, and Knowledge Bases
 * while configuring one analysis workflow. This wrapper keeps their title,
 * loading, empty, and table states aligned without giving each subsystem a
 * separate table style. Children continue to use the shared shadcn Table API;
 * this component changes only the surrounding card and descendant styling.
 */
import type { FC, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

interface TableCardProps {
  title: ReactNode
  /** Right side of the header band, e.g. a row count. */
  meta?: ReactNode
  children: ReactNode
}

export const TableCard: FC<TableCardProps> = ({ title, meta, children }) => (
  <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
    <div className="flex items-center justify-between gap-3 border-b border-[#eef2f6] px-6 py-4">
      <div className="text-sm font-semibold tracking-[-0.01em] text-slate-900">{title}</div>
      {meta != null && <div className="text-xs text-slate-400 tabular-nums">{meta}</div>}
    </div>
    <div className="[&_th]:h-9 [&_th]:bg-[#fafbfc] [&_th]:px-4 [&_th]:text-[11.5px] [&_th]:font-semibold [&_th]:text-slate-500 [&_td]:px-4 [&_td]:py-2.5 [&_td]:text-[12.5px] [&_tbody_tr:hover]:bg-slate-50">
      {children}
    </div>
  </div>
)

interface TableCardEmptyProps {
  title: string
  description: string
  action?: ReactNode
}

export const TableCardEmpty: FC<TableCardEmptyProps> = ({ title, description, action }) => (
  <div className="px-6 py-12 text-center">
    <div className="text-[13.5px] font-[550] text-slate-700">{title}</div>
    <div className="mx-auto mt-1 max-w-md text-[12.5px] text-slate-400">{description}</div>
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
)

export const TableCardLoading: FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center gap-2 px-6 py-12">
    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
    <span className="text-[13px] text-slate-500">{label}</span>
  </div>
)
